import { ApiError } from '@/lib/api';
import { apiErrorCode } from '@/lib/describeApiError';
import { isCertValid } from '@/utils/skill-matching';

export const CERTIFICATE_REQUIRED_CODE = 'CERTIFICATE_REQUIRED';
export const CERTIFICATE_OVERRIDE_FORBIDDEN_CODE = 'CERTIFICATE_OVERRIDE_FORBIDDEN';
export const SKILLS_CERTIFICATE_OVERRIDE_PERMISSION = 'skills.certificate_override';

export type CertificateBlockStatus = 'missing' | 'expired';

export interface CertificateBlockedItem {
  mechanicUserId: string;
  mechanicName: string;
  skillId: string;
  skillName: string;
  status: CertificateBlockStatus;
  expiresAt: string | null;
}

export interface CertificateRequiredError {
  code: typeof CERTIFICATE_REQUIRED_CODE;
  blocked: CertificateBlockedItem[];
}

export interface SkillGateOverride {
  reason: string;
}

/** Held-skill fields the picker may see; extra certificate fields are optional. */
export interface MechanicSkillCertificateLike {
  skillId: string;
  certifiedAt?: string | null;
  expiresAt?: string | null;
  certificate?: {
    status?: string | null;
    certifiedAt?: string | null;
    expiresAt?: string | null;
  } | null;
}

function bodyOf(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof ApiError)) return null;
  const { data } = error;
  return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
}

function readCode(error: unknown): string | null {
  const body = bodyOf(error);
  if (body) {
    if (typeof body.code === 'string' && body.code !== '') return body.code;
    if (typeof body.error === 'string' && body.error !== '') return body.error;
  }
  return apiErrorCode(error);
}

function parseBlocked(value: unknown): CertificateBlockedItem[] {
  if (!Array.isArray(value)) return [];
  const items: CertificateBlockedItem[] = [];
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Record<string, unknown>;
    if (row.status !== 'missing' && row.status !== 'expired') continue;
    if (typeof row.mechanicUserId !== 'string' || row.mechanicUserId === '') continue;
    if (typeof row.skillId !== 'string' || row.skillId === '') continue;
    items.push({
      mechanicUserId: row.mechanicUserId,
      mechanicName: typeof row.mechanicName === 'string' ? row.mechanicName : '',
      skillId: row.skillId,
      skillName: typeof row.skillName === 'string' ? row.skillName : '',
      status: row.status,
      expiresAt: typeof row.expiresAt === 'string' && row.expiresAt !== '' ? row.expiresAt : null,
    });
  }
  return items;
}

/** 409 CERTIFICATE_REQUIRED with the blocked mechanic/skill list, otherwise null. */
export function parseCertificateGateError(error: unknown): CertificateRequiredError | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  if (readCode(error) !== CERTIFICATE_REQUIRED_CODE) return null;
  return {
    code: CERTIFICATE_REQUIRED_CODE,
    blocked: parseBlocked(bodyOf(error)?.blocked),
  };
}

export function isCertificateOverrideForbiddenError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  return readCode(error) === CERTIFICATE_OVERRIDE_FORBIDDEN_CODE;
}

type SkillCertificateFlag = {
  requiresValidCertificate?: boolean | undefined;
  skill?: { requiresValidCertificate?: boolean | undefined } | undefined;
};

export function skillRequiresValidCertificate(skill: unknown): boolean {
  if (typeof skill !== 'object' || skill === null) return false;
  const row = skill as SkillCertificateFlag;
  return row.requiresValidCertificate === true || row.skill?.requiresValidCertificate === true;
}

export function certificateRequiredSkills<T>(skills: readonly T[]): T[] {
  return skills.filter((skill) => skillRequiresValidCertificate(skill));
}

/**
 * Advisory lock for the mechanic picker. A valid certificate is a held row
 * with certifiedAt set and a status other than expired; levels never block.
 */
export function mechanicCertificateIssue(
  mechanic: { skills: readonly MechanicSkillCertificateLike[] },
  requiredCertificateSkills: readonly { skillId: string }[],
  now: Date = new Date(),
): CertificateBlockStatus | null {
  for (const req of requiredCertificateSkills) {
    const held = mechanic.skills.find((skill) => skill.skillId === req.skillId);
    if (!held) return 'missing';

    const status = held.certificate?.status;
    if (status === 'expired') return 'expired';
    if (status === 'none' || status === 'missing') return 'missing';
    if (status === 'valid' || status === 'expiring') continue;

    const certifiedAt =
      held.certifiedAt !== undefined ? held.certifiedAt : held.certificate?.certifiedAt;
    if (certifiedAt === null || certifiedAt === '') return 'missing';

    const expiresAt = held.expiresAt ?? held.certificate?.expiresAt;
    if (!isCertValid(expiresAt, now)) return 'expired';
  }
  return null;
}
